import React, { useState } from 'react';
import { GitCompareArrows, Star, Check, Globe, X, Bookmark, Loader2, Share2 } from 'lucide-react';
import { getAuthToken } from '../../lib/tokenStorage';
import { pick } from '../../lib/uiHelpers';
import { COMPARE_ROWS, gymToSlug, safeId } from './data';

export const ComparePanel = ({ gyms, onRemove, onClose }) => {
  const [copied, setCopied] = useState(false);
  const [saveStatus, setSaveStatus] = useState('idle');
  if (gyms.length === 0) return null;

  const handleShare = () => {
    const slugs = gyms.map(gymToSlug).join(',');
    const url = `${window.location.origin}/hyrox?compare=${encodeURIComponent(slugs)}`;
    navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    });
  };

  const handleSave = async () => {
    const token = getAuthToken();
    if (!token) {
      setSaveStatus('error');
      setTimeout(() => setSaveStatus('idle'), 2500);
      return;
    }
    setSaveStatus('saving');
    try {
      const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/saved-comparisons`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ gyms: gyms.map((g) => ({ name: g.name, city: g.city, state: g.state })) }),
      });
      if (!resp.ok) throw new Error('Failed');
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 3000);
    } catch {
      setSaveStatus('error');
      setTimeout(() => setSaveStatus('idle'), 2500);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center" data-testid="compare-panel-overlay">
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-5xl max-h-[85vh] bg-[#0a0a0a] border border-zinc-800 overflow-hidden flex flex-col mx-4 mb-4 sm:mb-0" data-testid="compare-panel">
        <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-800 flex-shrink-0">
          <div className="flex items-center gap-3">
            <GitCompareArrows className="w-5 h-5 text-[#e8ff00]" />
            <h3 className="text-white font-heading text-lg">Compare Gyms</h3>
            <span className="text-zinc-500 text-xs">({gyms.length} selected)</span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleSave}
              disabled={saveStatus === 'saving'}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-heading uppercase tracking-wider border transition-all ${
                pick(saveStatus, {
                  saved: 'bg-green-500/10 border-green-500/40 text-green-400',
                  error: 'bg-red-500/10 border-red-500/40 text-red-400',
                }, 'border-zinc-700 text-zinc-400 hover:border-[#e8ff00]/40 hover:text-[#e8ff00]')
              }`}
              data-testid="compare-save-btn"
            >
              {pick(saveStatus, {
                saving: <><Loader2 className="w-3 h-3 animate-spin" /> Saving...</>,
                saved: <><Check className="w-3 h-3" /> Saved!</>,
                error: 'Login to Save',
              }, <><Bookmark className="w-3 h-3" /> Save</>)}
            </button>
            <button
              onClick={handleShare}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-heading uppercase tracking-wider border transition-all ${
                copied ? 'bg-[#e8ff00]/10 border-[#e8ff00]/40 text-[#e8ff00]' : 'border-zinc-700 text-zinc-400 hover:border-[#e8ff00]/40 hover:text-[#e8ff00]'
              }`}
              data-testid="compare-share-btn"
            >
              {copied ? <><Check className="w-3 h-3" /> Copied!</> : <><Share2 className="w-3 h-3" /> Share</>}
            </button>
            <button onClick={onClose} className="text-zinc-500 hover:text-white transition-colors p-1" data-testid="compare-panel-close">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="overflow-auto flex-1 p-6">
          <table className="w-full" data-testid="compare-table">
            <thead>
              <tr>
                <th className="text-left text-zinc-600 text-xs uppercase tracking-wider font-heading py-3 pr-4 w-36 align-top"></th>
                {gyms.map((g) => (
                  <th key={g.name} className="text-left py-3 px-3 align-top min-w-[200px]">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <p className="text-white font-heading text-sm leading-tight">{g.name}</p>
                        <p className="text-zinc-500 text-[11px] mt-0.5">{g.city}</p>
                      </div>
                      <button
                        onClick={() => onRemove(g)}
                        className="text-zinc-600 hover:text-red-400 transition-colors flex-shrink-0 mt-0.5"
                        data-testid={`compare-remove-${safeId(g.name)}`}
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {COMPARE_ROWS.map((row) => (
                <tr key={row.key} className="border-t border-zinc-800/60">
                  <td className="text-zinc-500 text-xs uppercase tracking-wider font-heading py-3 pr-4 align-top whitespace-nowrap">{row.label}</td>
                  {gyms.map((g) => {
                    const val = g[row.key];
                    return (
                      <td key={g.name} className="py-3 px-3 align-top">
                        {row.bool ? (
                          val ? (
                            <span className="inline-flex items-center gap-1 text-[#e8ff00] text-xs font-heading"><Check className="w-3 h-3" /> Yes</span>
                          ) : (
                            <span className="text-zinc-600 text-xs">No</span>
                          )
                        ) : row.key === 'rating' ? (
                          <span className="inline-flex items-center gap-1 text-white text-xs"><Star className="w-3 h-3 text-[#e8ff00]" /> {val}</span>
                        ) : row.key === 'phone' && val ? (
                          <a href={`tel:${val}`} className="text-[#e8ff00] text-xs hover:underline">{val}</a>
                        ) : (
                          <span className="text-zinc-300 text-xs leading-relaxed">{val || <span className="text-zinc-700">—</span>}</span>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
              <tr className="border-t border-zinc-800/60">
                <td className="text-zinc-500 text-xs uppercase tracking-wider font-heading py-3 pr-4 align-top">Website</td>
                {gyms.map((g) => (
                  <td key={g.name} className="py-3 px-3 align-top">
                    {g.website ? (
                      <a
                        href={g.website}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-[#e8ff00] text-xs font-heading hover:underline"
                        data-testid={`compare-visit-${safeId(g.name)}`}
                      >
                        Visit <Globe className="w-3 h-3" />
                      </a>
                    ) : <span className="text-zinc-700 text-xs">—</span>}
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
